import { useState } from "react";
import { Check } from "lucide-react";
import { Banner, Button, Field, Input, useToast } from "@/shared/rd";
import { Actions } from "../rd-local";
import { keys } from "@/lib/i18n/portal/index";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import {
  useConfirmStoreSubscriptionTopUpMutation,
  useRejectStoreSubscriptionTopUpMutation,
} from "@/shared/stores/services/mutations/useReviewStoreSubscriptionTopUpMutation";

type Props = {
  storeId: string;
  paymentId: string;
  t: (key: string) => string;
};

/** A rejection reason shorter than this is not a reason. Mirrors the DTO. */
const MIN_REASON_LENGTH = 2;

/**
 * Confirm / Reject for one wallet top-up request — the single implementation
 * behind both the store-scoped panel and the cross-store review queue.
 *
 * Confirm is the only path that moves money (the backend credits the wallet and
 * releases held orders oldest-first in the same transaction), so the surface
 * that renders this states that before the click, not after.
 *
 * Reject asks for its reason inline rather than stacking a modal: the request
 * is one row and the decision is one sentence. The reason is REQUIRED because
 * it is not an internal note — it is sent to the seller as a notification and
 * is the only way they learn what to fix before filing again.
 */
export function TopUpReviewActions({ storeId, paymentId, t }: Props) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  /** Which decision failed, for the banner's title and its Try again. */
  const [failed, setFailed] = useState<"confirm" | "reject" | null>(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const { mutateAsync: confirmTopUp, isPending: isConfirmPending } =
    useConfirmStoreSubscriptionTopUpMutation();
  const { mutateAsync: rejectTopUp, isPending: isRejectPending } =
    useRejectStoreSubscriptionTopUpMutation();
  const isBusy = isConfirmPending || isRejectPending;

  const toMessage = (caught: unknown) =>
    caught instanceof ApiJsonClientError
      ? caught.message || t(keys.errors.generic)
      : t(keys.errors.generic);

  const handleConfirm = async () => {
    setError(null);
    setFailed(null);
    try {
      await confirmTopUp({ storeId, paymentId });
      toast.show({ message: t(keys.admin.subscriptionTopUpConfirmSuccess) });
    } catch (caught) {
      setFailed("confirm");
      setError(toMessage(caught));
    }
  };

  const handleReject = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < MIN_REASON_LENGTH) {
      setReasonError(t(keys.admin.subscriptionTopUpRejectReasonRequired));
      return;
    }
    setError(null);
    setFailed(null);
    setReasonError(null);
    try {
      await rejectTopUp({ storeId, paymentId, reason: trimmed });
      toast.show({ message: t(keys.admin.subscriptionTopUpRejectSuccess) });
      setIsRejecting(false);
      setReason("");
    } catch (caught) {
      setFailed("reject");
      setError(toMessage(caught));
    }
  };

  const reasonId = `topup-reason-${paymentId}`;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {error && failed ? (
        <Banner
          tone="error"
          title={t(failed === "confirm" ? "admin.topUpConfirmFailedTitle" : "admin.topUpRejectFailedTitle")}
          actions={
            <Button
              size="sm"
              loading={isBusy}
              loadingLabel={t("admin.busyTrying")}
              onClick={() => void (failed === "confirm" ? handleConfirm() : handleReject())}
            >
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {error} {t("admin.nothingChanged")}
        </Banner>
      ) : null}

      {isRejecting ? (
        <Field
          label={t(keys.admin.subscriptionTopUpRejectReasonLabel)}
          htmlFor={reasonId}
          error={reasonError ?? undefined}
        >
          <Input
            id={reasonId}
            name="reason"
            required
            value={reason}
            invalid={Boolean(reasonError)}
            onChange={(event) => {
              setReason(event.target.value);
              setReasonError(null);
            }}
            placeholder={t(keys.admin.subscriptionTopUpRejectReasonPlaceholder)}
          />
        </Field>
      ) : null}

      <Actions>
        <Button
          variant="primary"
          size="sm"
          icon={Check}
          loading={isConfirmPending}
          loadingLabel={t("admin.busyConfirming")}
          disabled={isRejectPending}
          onClick={() => void handleConfirm()}
        >
          {t(keys.admin.subscriptionTopUpConfirmAction)}
        </Button>
        <Button
          variant={isRejecting ? "danger" : "secondary"}
          size="sm"
          loading={isRejectPending}
          loadingLabel={t("admin.busyRejecting")}
          disabled={isConfirmPending}
          onClick={() => {
            if (isRejecting) {
              void handleReject();
              return;
            }
            setIsRejecting(true);
            setReason("");
            setReasonError(null);
          }}
        >
          {t(keys.admin.subscriptionTopUpRejectAction)}
        </Button>
      </Actions>
    </div>
  );
}
